import moment from 'moment';

export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
export const TIME_FORMAT = 'HH:mm';
export const API_DATE_FORMAT = 'YYYY-MM-DD';
export const API_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * Most date fields in CIS are calendar dates (Doctrine `date` columns: issueDate,
 * validUntil, birthDate, dob, validFrom, invoiceDate, ...) but the API serialises them
 * as full timestamps, e.g. "2024-05-10T00:00:00+00:00".
 *
 * `moment(value)` / `new Date(value)` re-interpret that instant in the browser's own
 * timezone, which moves the calendar day for anyone behind the serialised offset — a
 * user in Mexico (UTC-6) sees 09/05/2024 for a certificate issued on the 10th. Worse,
 * an edit form that hydrates a Datepicker that way sends the shifted day back on save.
 *
 * `moment.parseZone` keeps the offset that arrived with the string instead of
 * converting, so the calendar day reads the same in every timezone. Values that are
 * already `Date` objects (picked in a Datepicker) keep their local wall clock, so the
 * same helpers are safe on both sides of a round trip.
 */
export const parseStored = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = moment.parseZone(value);

  return parsed.isValid() ? parsed : null;
};

/** Display a calendar date. Renders identically in every timezone. */
export const formatDate = (value, format = DATE_FORMAT) => {
  const parsed = parseStored(value);

  return parsed ? parsed.format(format) : null;
};

/**
 * Display a true instant (Doctrine `datetime` columns: createdAt, updatedAt, ...) in
 * the viewer's own timezone — for those the local conversion is the correct behaviour.
 */
export const formatDateTime = (value, format = DATE_TIME_FORMAT) => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = moment(value);

  return parsed.isValid() ? parsed.format(format) : null;
};

/**
 * The time of day of a true instant, in the viewer's own timezone — for stamping
 * something that just happened ("saved at 14:32"), where the date is today and
 * spelling it out adds nothing.
 */
export const formatTime = (value) => formatDateTime(value, TIME_FORMAT);

/**
 * Display a span of calendar days — a meeting held over several of them, say.
 *
 * The two ends collapse into one whenever they render the same, so a one day
 * span reads as a single date and a span inside one month reads as that month
 * instead of saying it twice. Either end on its own is shown as it is.
 */
export const formatDateRange = (from, to, format = DATE_FORMAT) => {
  const start = formatDate(from, format);
  const end = formatDate(to, format);

  if (!start) return end;
  if (!end || start === end) return start;

  return `${start} - ${end}`;
};

/** Serialise a calendar date for the API without shifting the day. */
export const toApiDate = (value, format = API_DATE_FORMAT) => formatDate(value, format);

/**
 * A `Date` at local midnight of the stored calendar day — what a `<Datepicker>` needs
 * so it shows the stored day and round-trips back through `toApiDate` unchanged.
 */
export const toDateInput = (value) => {
  const parsed = parseStored(value);

  return parsed ? new Date(parsed.year(), parsed.month(), parsed.date()) : null;
};

/**
 * Same as `toDateInput`, but keeps the time of day too — for datetime fields edited in
 * a `<Datepicker showTimeSelect>`, where the stored wall clock is what the user typed.
 */
export const toDateTimeInput = (value) => {
  const parsed = parseStored(value);

  return parsed
    ? new Date(
        parsed.year(),
        parsed.month(),
        parsed.date(),
        parsed.hour(),
        parsed.minute(),
        parsed.second(),
      )
    : null;
};
